import { useMemo, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DataTable,
  FormInput,
  FormSelect,
  ImageUpload,
  PageHeader,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { UploadFolder, resolveUploadUrl } from "@/services/filesService";
import StandardImage from "@/components/shared/StandardImage";
import {
  useAdminCategories,
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation,
} from "@/hooks/queries";
import type { AdminCategory } from "@/types/category";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

const emptyForm = {
  name: "",
  description: "",
  parentId: null as string | null,
  imageUrl: "",
  isActive: true,
  commissionPercentage: 0,
};

function CategoryThumbnail({ name, imageUrl }: { name: string; imageUrl?: string }) {
  return (
    <StandardImage
      src={imageUrl}
      alt={name}
      standard="category"
      className="h-10 w-10"
      rounded="lg"
    />
  );
}

const statusBadge: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  inactive: "bg-muted text-muted-foreground border-border",
};

const AdminCategories = () => {
  const { toast } = useToast();
  const { data, isLoading } = useAdminCategories();
  const createMutation = useCreateCategoryMutation();
  const updateMutation = useUpdateCategoryMutation();
  const deleteMutation = useDeleteCategoryMutation();
  const categories = data?.items ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminCategory | null>(null);
  const [form, setForm] = useState(emptyForm);

  const table = useDataTable<AdminCategory>({
    data: categories,
    searchKeys: ["name"],
  });

  const parentName = (id: string | null) =>
    id ? categories.find((c) => c.id === id)?.name ?? "—" : "Root";

  const parentOptions = useMemo(
    () => [
      { label: "None (Root)", value: "none" },
      ...categories
        .filter((c) => c.id !== editing?.id)
        .map((c) => ({ label: c.name, value: c.id })),
    ],
    [categories, editing?.id]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (cat: AdminCategory) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      description: cat.description ?? "",
      parentId: cat.parentId,
      imageUrl: cat.imageUrl ?? "",
      isActive: cat.isActive,
      commissionPercentage: cat.commissionPercentage,
    });
    setDialogOpen(true);
  };

  const nextDisplayOrder = () =>
    categories.reduce((max, c) => Math.max(max, c.displayOrder), 0) + 1;

  const handleSave = async () => {
    if (!form.name.trim()) return;

    try {
      if (editing) {
        await updateMutation.mutateAsync({
          id: Number(editing.id),
          dto: {
            name: form.name,
            description: form.description || undefined,
            imageUrl: form.imageUrl || undefined,
            parentCategoryId: form.parentId ? Number(form.parentId) : undefined,
            displayOrder: editing.displayOrder,
            isActive: form.isActive,
            commissionPercentage: form.commissionPercentage,
          },
        });
        toast({ title: "Category updated" });
      } else {
        await createMutation.mutateAsync({
          name: form.name,
          description: form.description || undefined,
          imageUrl: form.imageUrl || undefined,
          parentCategoryId: form.parentId ? Number(form.parentId) : undefined,
          displayOrder: nextDisplayOrder(),
          commissionPercentage: form.commissionPercentage,
        });
        toast({ title: "Category created" });
      }
      setDialogOpen(false);
    } catch (error) {
      toast({ title: "Could not save category", description: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMutation.mutateAsync(Number(deleteId));
      toast({ title: "Category deleted" });
      setDeleteId(null);
    } catch (error) {
      toast({
        title: "Cannot delete category",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const columns: DataTableColumn<AdminCategory>[] = [
    {
      id: "name",
      header: "Category",
      accessorKey: "name",
      sortable: true,
      mobilePriority: 1,
      cell: (cat) => (
        <div className="flex items-center gap-2">
          <CategoryThumbnail name={cat.name} imageUrl={cat.imageUrl} />
          <span className="font-semibold">{cat.name}</span>
        </div>
      ),
    },
    {
      id: "parentId",
      header: "Parent",
      mobileLabel: "Parent",
      mobilePriority: 2,
      cell: (cat) => <span className="text-sm text-muted-foreground">{parentName(cat.parentId)}</span>,
    },
    {
      id: "productCount",
      header: "Products",
      accessorKey: "productCount",
      sortable: true,
      align: "right",
      mobileLabel: "Products",
      mobilePriority: 3,
    },
    {
      id: "isActive",
      header: "Status",
      accessorKey: "isActive",
      sortable: true,
      mobileLabel: "Status",
      mobilePriority: 5,
      cell: (cat) => (
        <Badge variant="outline" className={cat.isActive ? statusBadge.active : statusBadge.inactive}>
          {cat.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      hideOnMobile: true,
      cell: (cat) => (
        <div className="flex items-center justify-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(cat)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => setDeleteId(cat.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Categories"
        description="Manage product categories for the marketplace."
        actions={
          <Button className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add Category
          </Button>
        }
      />

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-sm">
          Upload a category image for storefront and admin display.
        </AlertDescription>
      </Alert>

      {isLoading ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : (
      <DataTable
        title={`All Categories (${categories.length})`}
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        renderMobileCard={(cat) => (
          <div className="rounded-lg border border-border p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <CategoryThumbnail name={cat.name} imageUrl={cat.imageUrl} />
                <div>
                  <p className="font-semibold">{cat.name}</p>
                  <p className="text-xs text-muted-foreground">{parentName(cat.parentId)}</p>
                </div>
              </div>
              <Badge variant="outline" className={cat.isActive ? statusBadge.active : statusBadge.inactive}>
                {cat.isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{cat.productCount} products</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(cat)}>
                <Pencil className="mr-1 h-3 w-3" /> Edit
              </Button>
              <Button size="sm" variant="destructive" className="flex-1" onClick={() => setDeleteId(cat.id)}>
                <Trash2 className="mr-1 h-3 w-3" /> Delete
              </Button>
            </div>
          </div>
        )}
      />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <FormInput
              label="Name"
              requiredMark
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <FormInput
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <ImageUpload
              label="Category image"
              value={form.imageUrl}
              onChange={(url) => setForm({ ...form, imageUrl: url })}
              folder={UploadFolder.Categories}
            />
            <FormSelect
              label="Parent Category"
              value={form.parentId ?? "none"}
              onValueChange={(v) => setForm({ ...form, parentId: v === "none" ? null : v })}
              options={parentOptions}
            />
            <FormInput
              label="Commission (%)"
              type="number"
              value={form.commissionPercentage}
              onChange={(e) => setForm({ ...form, commissionPercentage: Number(e.target.value) })}
            />
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Active</Label>
              <Switch checked={form.isActive} onCheckedChange={(v) => setForm({ ...form, isActive: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!form.name.trim() || createMutation.isPending || updateMutation.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteId)} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the category. Categories with products cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCategories;
